import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { dentroDaMeta, periodoMeta } from '../lib/metas';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Banknote, MessageCircle, UserPlus, X } from 'lucide-react';
import { fetchPayrollAlerts } from '../services/payrollService';
import { hojeLocal } from '../../supabase/functions/_shared/recebiveis';
import { useGlobalChat } from './GlobalChatContext';

export interface Notification {
    id: string;
    type: 'message' | 'lead' | 'payroll';
    title: string;
    description: string;
    read: boolean;
    createdAt: Date;
    link?: string;
    data?: Record<string, unknown>;
}

interface NotificationsContextType {
    notifications: Notification[];
    unreadCount: number;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearAll: () => void;
    addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, company, permissions } = useAuth();
    const navigate = useNavigate();
    const { openChat } = useGlobalChat();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [hasLoaded, setHasLoaded] = useState(false);

    // Carregar do localStorage quando o usuário estiver disponível
    useEffect(() => {
        if (user && !hasLoaded) {
            try {
                const saved = localStorage.getItem(`crm_notifications_${user.id}`);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (Array.isArray(parsed)) {
                        setNotifications(parsed.slice(0, 50));
                    }
                }
            } catch (e) {
                console.error('Failed to parse notifications from localStorage', e);
            }
            setHasLoaded(true);
        }
    }, [user, hasLoaded]);

    // Sincronizar entre abas
    useEffect(() => {
        if (!user) return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === `crm_notifications_${user.id}` && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    if (Array.isArray(parsed)) {
                        // Apenas atualiza o estado para não causar loop
                        setNotifications(parsed);
                    }
                } catch (err) {
                    console.error('Error parsing notifications from storage event', err);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [user]);

    // Salvar no localStorage sempre que mudar
    useEffect(() => {
        if (user && hasLoaded) {
            localStorage.setItem(`crm_notifications_${user.id}`, JSON.stringify(notifications));
        }
    }, [notifications, user, hasLoaded]);

    const unreadCount = notifications.filter(n => !n.read).length;

    const addNotification = (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
        const newNotification: Notification = {
            ...notification,
            id: Math.random().toString(36).substr(2, 9),
            createdAt: new Date(),
            read: false,
        };

        setNotifications(prev => {
            const updated = [newNotification, ...prev];
            return updated.slice(0, 50); // Limite de 50 notificações para não estourar storage
        });

        // Visual Toast Notification
        toast.custom((t) => (
            <div
                onClick={() => {
                    if (notification.type === 'message' && notification.data?.leadObj) {
                        openChat(notification.data.leadObj as any);
                        toast.dismiss(t);
                    } else if (notification.link) {
                        navigate(notification.link as string);
                        toast.dismiss(t);
                    }
                }}
                onPointerDown={(e) => {
                    // Fallback trigger in case onClick is swallowed (nunca ao apertar o botão de fechar)
                    if ((e.target as HTMLElement).closest('button')) return;
                    if (notification.type === 'message' && notification.data?.leadObj) {
                        openChat(notification.data.leadObj as any);
                        toast.dismiss(t);
                    } else if (notification.link) {
                        navigate(notification.link as string);
                        toast.dismiss(t);
                    }
                }}
                className={`relative overflow-hidden flex w-full min-w-[320px] max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 dark:border-gray-700 p-4 gap-3 pointer-events-auto transition-transform hover:scale-[1.02] cursor-pointer ${notification.type === 'message' ? 'ring-1 ring-purple-100 dark:ring-purple-900/50' : notification.type === 'payroll' ? 'ring-1 ring-amber-100 dark:ring-amber-900/50' : 'ring-1 ring-green-100 dark:ring-green-900/50'}`}
            >
                {/* Accent line on left */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${notification.type === 'message' ? 'bg-purple-500' : notification.type === 'payroll' ? 'bg-amber-500' : 'bg-green-500'}`} />

                <div className={`mt-0.5 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-inner ${notification.type === 'message' ? 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white' : notification.type === 'payroll' ? 'bg-gradient-to-br from-amber-400 to-orange-600 text-white' : 'bg-gradient-to-br from-green-400 to-emerald-600 text-white'}`}>
                    {notification.type === 'message' ? <MessageCircle size={18} /> : notification.type === 'payroll' ? <Banknote size={18} /> : <UserPlus size={18} />}
                </div>

                <div className="flex-1 min-w-0 pr-6">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {notification.title}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                        {notification.description}
                    </p>
                    {notification.link && (
                        <p className={`text-[11px] font-bold mt-1.5 ${notification.type === 'message' ? 'text-purple-600 dark:text-purple-400' : 'text-green-700 dark:text-green-400'}`}>
                            Clique para abrir →
                        </p>
                    )}
                </div>

                <button aria-label="Fechar"
                    onClick={(e) => {
                        e.stopPropagation();
                        toast.dismiss(t);
                    }}
                    className="absolute top-2 right-2 p-1.5 text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-full transition-colors z-10"
                >
                    <X size={12} />
                </button>
            </div>
        ), { duration: 4000 });
    };

    const markAsRead = (id: string) => {
        setNotifications(prev =>
            prev.map(n => (n.id === id ? { ...n, read: true } : n))
        );
    };

    const markAllAsRead = () => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    };

    const clearAll = () => {
        setNotifications([]);
    };

    // Avisos da folha (rascunho para conferir, pagamentos vencendo): só para quem tem payroll.access,
    // no máximo uma vez por dia para cada aviso.
    const canPayroll = (permissions || []).includes('payroll.access');
    useEffect(() => {
        if (!user || !company?.id || !canPayroll || !hasLoaded) return;
        let cancelado = false;
        (async () => {
            try {
                const alertas = await fetchPayrollAlerts(company.id);
                if (cancelado || alertas.length === 0) return;
                const chave = `crm_payroll_alerts_${user.id}`;
                let vistos: Record<string, string> = {};
                try { vistos = JSON.parse(localStorage.getItem(chave) || '{}'); } catch { vistos = {}; }
                const hoje = hojeLocal();
                for (const a of alertas) {
                    if (vistos[a.key] === hoje) continue;
                    vistos[a.key] = hoje;
                    addNotification({ type: 'payroll', title: a.title, description: a.description, link: a.link });
                }
                localStorage.setItem(chave, JSON.stringify(vistos));
            } catch (err) {
                console.error('Erro ao verificar avisos da folha:', err);
            }
        })();
        return () => { cancelado = true; };
        // addNotification muda a cada render; o aviso depende só de quem é o usuário e da permissão
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, company?.id, canPayroll, hasLoaded]);

    // Subscrições via websocket
    useEffect(() => {
        if (!user || !company || !supabase) return;

        // Channel for Chat Messages - Filtered by receiver_id for tenant isolation
        const chatChannel = supabase
            .channel(`notifications-chat-${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                    filter: `receiver_id=eq.${user.id}`
                },
                async (payload: Record<string, unknown>) => {
                    const newMessage = payload.new as any;

                    // Avoid notification if we are already on the chat page
                    if (window.location.hash === '#/chat' || window.location.pathname.includes('/chat')) {
                        // Keep bell state without blocking
                    }

                    // Try to get name, default to 'Alguém'
                    let senderName = 'Alguém';
                    try {
                        const { data: senderData } = await supabase
                            .from('users')
                            .select('name')
                            .eq('id', newMessage.sender_id)
                            .single();
                        if (senderData) senderName = senderData.name;
                    } catch (e) {
                        console.error('Error fetching sender name for notification', e);
                    }

                    addNotification({
                        type: 'message',
                        title: `Nova mensagem de ${senderName}`,
                        description: newMessage.message,
                        link: '/chat',
                        data: { senderId: newMessage.sender_id }
                    });
                }
            )
            .subscribe();

        // Channel for Leads
        const leadsChannel = supabase
            .channel(`notifications-leads-${company.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'leads',
                    filter: `company_id=eq.${company.id}`,
                },
                (payload: Record<string, unknown>) => {
                    const newLead = payload.new as any;
                    // Notify se desatribuído (everyone) OU atribuído a este user
                    if (!newLead.user_id || newLead.user_id === user.id) {
                        addNotification({
                            type: 'lead',
                            title: 'Novo Lead',
                            description: `${newLead.name} - ${newLead.company_name || 'Sem empresa'}`,
                            link: `/leads?openLead=${newLead.id}`,
                            data: { leadId: newLead.id }
                        });
                    }
                }
            )
            .subscribe();

        // Channel for Calendar Events
        const calendarChannel = supabase
            .channel(`notifications-calendar-${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'calendar_events',
                    filter: `user_id=eq.${user.id}`,
                },
                (payload: Record<string, unknown>) => {
                    const newEvent = payload.new as any;
                    addNotification({
                        type: 'lead',
                        title: 'Novo Evento na Agenda',
                        description: `${newEvent.title} - ${new Date(newEvent.start_time).toLocaleString()}`,
                        link: '/calendar',
                        data: { eventId: newEvent.id }
                    });
                }
            )
            .subscribe();

        // Channel for WhatsApp Messages
        const whatsappChannel = supabase
            .channel(`notifications-whatsapp-${company.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'lead_whatsapp_messages',
                    filter: `company_id=eq.${company.id}`,
                },
                async (payload: Record<string, unknown>) => {
                    const newMessage = payload.new as any;

                    if (newMessage.direction === 'INBOUND') {
                        let senderName = 'Contato WhatsApp';
                        let leadObj = null;
                        try {
                            const { data: leadData } = await supabase
                                .from('leads')
                                .select('*')
                                .eq('id', newMessage.lead_id)
                                .single();
                            if (leadData) {
                                senderName = leadData.name || leadData.phone || senderName;
                                leadObj = leadData;
                            }
                        } catch (e) {
                            console.error('Error fetching lead name for whatsapp notification', e);
                        }

                        let messageText = newMessage.message_body || '';
                        if (newMessage.message_type === 'image') messageText = '📷 Imagem recebida';
                        else if (newMessage.message_type === 'video') messageText = '🎥 Vídeo recebido';
                        else if (newMessage.message_type === 'audio') messageText = '🎵 Áudio recebido';
                        else if (newMessage.message_type === 'document') messageText = '📄 Documento recebido';

                        addNotification({
                            type: 'message',
                            title: `WhatsApp: ${senderName}`,
                            description: messageText,
                            link: `/leads?openLead=${newMessage.lead_id}&tab=chat`,
                            data: { leadId: newMessage.lead_id, source: 'whatsapp', leadObj }
                        });
                    }
                }
            )
            .subscribe();

        // Channel for Sales (Goal Hit Logic)
        const salesChannel = supabase
            .channel(`notifications-sales-${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'sales',
                    filter: `user_id=eq.${user.id}`,
                },
                async (payload: Record<string, unknown>) => {
                    const newSale = payload.new;
                    const newSaleValue = Number(newSale.value);

                    // Check if there is an active goal for this user
                    // end_date é meia-noite UTC do último dia: busca com folga e confere o dia local abaixo
                    const agora = new Date();
                    const ontem = new Date(agora.getTime() - 2 * 86400000).toISOString();
                    const { data: goals } = await supabase
                        .from('goals')
                        .select('*')
                        .eq('company_id', company.id)
                        .eq('type', 'individual')
                        .eq('assigned_to', user.id)
                        .lte('start_date', agora.toISOString())
                        .gte('end_date', ontem);

                    if (goals && goals.length > 0) {
                        // For each goal, check if we just crossed it
                        for (const goal of goals) {
                            // Aviso em R$; metas de portas acompanham pelo painel
                            if (goal.metric_type === 'doors' || !dentroDaMeta(agora, goal)) continue;
                            const periodo = periodoMeta(goal);
                            // Calculate total sales for this user in the goal period, EXCLUDING current new sale (to see previous state)
                            // or simple approach: sum all sales in period, if sum >= goal AND (sum - newsale) < goal -> WE HIT IT!

                            const { data: salesInPeriod } = await supabase
                                .from('sales')
                                .select('value')
                                .eq('user_id', user.id)
                                .gte('sale_date', periodo.inicio.toISOString())
                                .lte('sale_date', periodo.fim.toISOString());

                            const totalSales = salesInPeriod?.reduce((acc, curr) => acc + Number(curr.value), 0) || 0;
                            const previousTotal = totalSales - newSaleValue;

                            if (totalSales >= goal.target_value && previousTotal < goal.target_value) {
                                addNotification({
                                    type: 'lead', // Reusing green icon
                                    title: '🎉 PARABÉNS! META BATIDA!',
                                    description: `Você atingiu a meta "${goal.title}" com a nova venda de R$ ${newSaleValue}!`,
                                    link: '/dashboard',
                                    data: { goalId: goal.id }
                                });
                            }
                        }
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(chatChannel);
            supabase.removeChannel(leadsChannel);
            supabase.removeChannel(calendarChannel);
            supabase.removeChannel(salesChannel);
            supabase.removeChannel(whatsappChannel);
        };
    }, [user, company]);

    // Avisos de tarefas próximas do vencimento (15 min antes).
    // Busca no banco a cada 5 min as tarefas dos próximos 20 min e confere a cada minuto
    // só na memória; aba em segundo plano não consulta (antes era 1 consulta por minuto
    // em toda aba aberta — a maior carga em lead_tasks).
    useEffect(() => {
        if (!user || !company || !supabase) return;

        const LOOKAHEAD_MS = 15 * 60000;
        const FETCH_EVERY_MS = 5 * 60000;
        let upcoming: Array<{ id: number; title: string; due_date: string }> = [];
        let lastFetch = 0;

        const fetchUpcoming = async () => {
            const now = Date.now();
            const { data } = await supabase
                .from('lead_tasks')
                .select('id, title, due_date')
                .eq('assigned_to', user.id)
                .eq('status', 'pending')
                .gte('due_date', new Date(now).toISOString())
                .lte('due_date', new Date(now + FETCH_EVERY_MS + LOOKAHEAD_MS).toISOString());
            upcoming = data || [];
            lastFetch = now;
        };

        const checkExpiringTasks = async () => {
            if (document.visibilityState === 'hidden') return;
            try {
                if (Date.now() - lastFetch >= FETCH_EVERY_MS) await fetchUpcoming();

                const now = Date.now();
                const tasks = upcoming.filter(t => {
                    const due = new Date(t.due_date).getTime();
                    return due >= now && due <= now + LOOKAHEAD_MS;
                });

                if (tasks.length > 0) {
                    const notifiedTasksStr = localStorage.getItem(`crm_notified_tasks_${user.id}`) || '[]';
                    const notifiedTasks = new Set<number>(JSON.parse(notifiedTasksStr));

                    let newNotifications = false;

                    tasks.forEach(task => {
                        if (!notifiedTasks.has(task.id)) {
                            addNotification({
                                type: 'lead',
                                title: '⏰ Tarefa Próxima do Vencimento',
                                description: `A tarefa "${task.title}" vence às ${new Date(task.due_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`,
                                link: '/leads',
                                data: { taskId: task.id }
                            });
                            notifiedTasks.add(task.id);
                            newNotifications = true;
                        }
                    });

                    if (newNotifications) {
                        localStorage.setItem(`crm_notified_tasks_${user.id}`, JSON.stringify(Array.from(notifiedTasks)));
                    }
                }
            } catch (err) {
                console.error('Error checking expiring tasks:', err);
            }
        };

        // Verifica agora, a cada 1 minuto (em memória) e ao voltar para a aba
        checkExpiringTasks();
        const intervalId = setInterval(checkExpiringTasks, 60000);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') checkExpiringTasks();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [user, company]);

    return (
        <NotificationsContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllAsRead,
            clearAll,
            addNotification
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};
